import { useEffect } from "react";
import { toast } from "sonner";

// Lote 63 (pedido do Robson, 05/10/2026): o app aberto — aba esquecida ou PWA instalado —
// não pegava o código novo depois de um deploy (o useAutoRefresh só recarrega DADOS, não o
// código). Colegas que estão testando não vão ficar limpando cache na mão, então a
// atualização tem que ser sozinha.
//
// Como funciona: o código rodando agora sabe qual arquivo main.<hash>.js o carregou; o
// servidor (/api/version) diz qual é o main.<hash>.js do deploy atual. Se forem diferentes,
// o app recarrega. A checagem roda ao abrir, ao voltar pra tela, ao voltar a ter internet e
// a cada 10 minutos com a tela aberta.
//
// Recarregar no meio de um formulário perderia o que a pessoa digitou — então só recarrega
// sozinho quando é seguro: a aba está em segundo plano, OU a pessoa está parada há mais de
// 60s, sem janela (diálogo) aberta e sem cursor num campo de texto. Fora disso, mostra um
// aviso "Atualizar agora" e recarrega assim que a aba for pra segundo plano.
const CHECK_EVERY_MS = 10 * 60 * 1000;
const IDLE_BEFORE_RELOAD_MS = 60 * 1000;
const LOOP_GUARD_KEY = "iapauta-version-reload";

function runningVersion() {
  try {
    const scripts = Array.from(document.querySelectorAll("script[src]"));
    for (const s of scripts) {
      const m = /\/static\/js\/(main\.[A-Za-z0-9]+\.js)/.exec(s.getAttribute("src") || "");
      if (m) return m[1];
    }
  } catch {
    /* ignora */
  }
  return "";
}

function typingInField() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export function useAppVersionCheck() {
  useEffect(() => {
    const current = runningVersion();
    if (!current) return undefined; // modo desenvolvimento / sem hash: nada a comparar

    let lastActivity = 0; // 0 = nenhuma interação desde que a tela abriu → conta como "parado"
    let pendingVersion = "";
    const markActivity = () => {
      lastActivity = Date.now();
    };

    const reload = (version) => {
      // Trava anti-loop: se já recarregamos por essa versão há menos de 2 min e ainda está
      // diferente (ex.: o navegador devolveu uma cópia antiga), não insiste.
      try {
        const prev = JSON.parse(sessionStorage.getItem(LOOP_GUARD_KEY) || "null");
        if (prev && prev.version === version && Date.now() - prev.at < 120000) return;
        sessionStorage.setItem(LOOP_GUARD_KEY, JSON.stringify({ version, at: Date.now() }));
      } catch {
        /* sem sessionStorage: segue sem a trava */
      }
      window.location.reload();
    };

    const safeToReload = () => {
      if (document.visibilityState === "hidden") return true;
      const idle = Date.now() - lastActivity > IDLE_BEFORE_RELOAD_MS;
      const dialogOpen = Boolean(document.querySelector('[role="dialog"], [role="alertdialog"]'));
      return idle && !dialogOpen && !typingInField();
    };

    const onNewVersion = (version) => {
      pendingVersion = version;
      if (safeToReload()) {
        reload(version);
        return;
      }
      toast("Nova versão do IApauta disponível", {
        id: "app-update",
        duration: Infinity,
        description: "Atualizamos o app. Ele recarrega sozinho quando você terminar o que está fazendo.",
        action: { label: "Atualizar agora", onClick: () => reload(version) },
      });
    };

    const check = async () => {
      try {
        const r = await fetch("/api/version", { cache: "no-store" });
        if (!r.ok) return;
        const { version } = await r.json();
        if (version && version !== current) onNewVersion(version);
      } catch {
        /* sem internet / servidor reiniciando: tenta de novo no próximo gatilho */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        check();
      } else if (pendingVersion) {
        reload(pendingVersion); // aba foi pra segundo plano com atualização pendente
      }
    };

    const first = setTimeout(check, 3000);
    const interval = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    ["keydown", "pointerdown", "input"].forEach((ev) => window.addEventListener(ev, markActivity, true));

    return () => {
      clearTimeout(first);
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      ["keydown", "pointerdown", "input"].forEach((ev) => window.removeEventListener(ev, markActivity, true));
    };
  }, []);
}
